import React, { useEffect, useMemo, useState } from "react"
import { IoCloseOutline } from "react-icons/io5"
import { RiSearchLine } from "react-icons/ri"

import { XMarkIcon } from "@heroicons/react/24/outline"

import {
  ExamplePlugin,
  examplePlugins,
  getExamplePluginsByCategory,
  getExamplePluginsByDifficulty,
  searchExamplePlugins,
} from "@fuse/utils/examplePlugins"

interface ExamplePluginsModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectExample: (example: ExamplePlugin) => void
}

const ExamplePluginsModal: React.FC<ExamplePluginsModalProps> = ({
  isOpen,
  onClose,
  onSelectExample,
}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string>("all")
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>("all")
  const [filteredPlugins, setFilteredPlugins] =
    useState<ExamplePlugin[]>(examplePlugins)

  console.log("[ExamplePluginsModal] Rendering with props:", {
    isOpen,
    onClose: !!onClose,
    onSelectExample: !!onSelectExample,
  })

  // Filter plugins based on search, category, and difficulty
  useEffect(() => {
    let filtered = examplePlugins

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = searchExamplePlugins(searchQuery)
    }

    // Apply category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (plugin) => plugin.category === selectedCategory,
      )
    }

    // Apply difficulty filter
    if (selectedDifficulty !== "all") {
      filtered = filtered.filter(
        (plugin) => plugin.difficulty === selectedDifficulty,
      )
    }

    setFilteredPlugins(filtered)
  }, [searchQuery, selectedCategory, selectedDifficulty])

  const categories = getExamplePluginsByCategory()
  const categoryNames = Object.keys(categories)

  const handleSelectExample = (example: ExamplePlugin) => {
    onSelectExample(example)
    onClose()
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return "bg-green-100 text-green-800"
      case "intermediate":
        return "bg-yellow-100 text-yellow-800"
      case "advanced":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return "🌱"
      case "intermediate":
        return "🚀"
      case "advanced":
        return "⚡"
      default:
        return "📦"
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 flex justify-center bg-black bg-opacity-50 z-[100] items-end md:items-center">
      {/* Debug overlay */}
      <div className="absolute top-4 left-4 bg-yellow-400 text-black p-2 rounded text-xs z-[101]">
        Modal is open! isOpen={isOpen.toString()}
      </div>

      <div
        className="w-full md:max-w-4xl bg-[#2F3152] rounded-t-2xl md:rounded-2xl relative flex flex-col cursor-auto h-[85vh] max-h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-600">
          <div className="relative flex items-center justify-center mb-4">
            <div className="text-[#C2C3CC] text-xl font-medium text-center w-full">
              Example Plugins
            </div>
            <button
              className="absolute right-0 p-1 hover:bg-gray-700 rounded"
              onClick={onClose}
            >
              <IoCloseOutline color="#C2C3CC" size={24} />
            </button>
          </div>

          <p className="text-[#8B8D97] text-sm text-center mb-4">
            Choose from our curated collection of plugin examples to get started
            quickly
          </p>

          {/* Search */}
          <div className="relative mb-4">
            <RiSearchLine className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#8B8D97]" />
            <input
              type="text"
              className="w-full bg-[#5A5E83] bg-opacity-20 rounded-full py-3 pl-11 pr-4 placeholder-[#8B8D97] text-[#C2C3CC] focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search examples..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bg-[#5A5E83] bg-opacity-20 text-[#C2C3CC] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categoryNames.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>

            {/* Difficulty Filter */}
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="bg-[#5A5E83] bg-opacity-20 text-[#C2C3CC] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredPlugins.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🔍</div>
              <h3 className="text-[#C2C3CC] text-lg font-medium mb-2">
                No examples found
              </h3>
              <p className="text-[#8B8D97] text-sm">
                Try adjusting your search or filters to find what you're looking
                for
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredPlugins.map((plugin) => (
                <div
                  key={plugin.id}
                  className="bg-[#3A3E5C] rounded-lg p-6 hover:bg-[#424768] transition-colors cursor-pointer border border-gray-600 hover:border-blue-500"
                  onClick={() => handleSelectExample(plugin)}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-[#C2C3CC] text-lg font-semibold mb-1">
                        {plugin.name}
                      </h3>
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(plugin.difficulty)}`}
                        >
                          {getDifficultyIcon(plugin.difficulty)}{" "}
                          {plugin.difficulty}
                        </span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          {plugin.category}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-[#8B8D97] text-sm mb-4 line-clamp-3">
                    {plugin.description}
                  </p>

                  {/* Preview */}
                  {plugin.preview && (
                    <div className="mb-4 p-3 bg-[#2A2E4A] rounded border-l-4 border-blue-500">
                      <p className="text-[#C2C3CC] text-xs">
                        <span className="font-medium">Preview:</span>{" "}
                        {plugin.preview}
                      </p>
                    </div>
                  )}

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {plugin.tags.slice(0, 4).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-[#5A5E83] bg-opacity-30 text-[#C2C3CC] rounded text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                    {plugin.tags.length > 4 && (
                      <span className="px-2 py-1 bg-[#5A5E83] bg-opacity-30 text-[#8B8D97] rounded text-xs">
                        +{plugin.tags.length - 4} more
                      </span>
                    )}
                  </div>

                  {/* Action */}
                  <div className="flex items-center justify-between">
                    <div className="text-[#8B8D97] text-xs">
                      Click to use this template
                    </div>
                    <div className="text-blue-400 text-sm font-medium">
                      Use Template →
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-600 bg-[#2A2E4A]">
          <div className="flex items-center justify-between text-sm">
            <div className="text-[#8B8D97]">
              {filteredPlugins.length} example
              {filteredPlugins.length !== 1 ? "s" : ""} available
            </div>
            <div className="text-[#8B8D97]">
              💡 These examples include complete code for all three views
              (Guest, Host, Config)
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExamplePluginsModal
